import { useState, useContext } from "react";

import { 
  PdfIcon,
  CameraIcon,
  XlsIcon,
  CloseIcon
} from './purchaseHistory.index';

import { Button } from "@aidron/aidron-ds";

import { Default } from "../../../components/Default/Default";

import { i18n } from "../../../translate/i18n";

import { 
  LoadingContent, 
  CancelModalButton,
  OrderDetailsActions,
  OrderDetailsTable,
  OrderDetailsHeader,
} from "../../../components/SharedComponents/sharedComponents.index";

import { HEADER_STRATEGY_CONSTANTS, HeaderWithFilters } from "../../../viewModel/strategy/headers/HeaderWithFilters/headerWithFilters.strategy";

import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";

import { usePurchaseRecommendationViewModel } from "../../../viewModel/usePurchaseManagementViewModel";
import { useStoreInformation } from "../../../viewModel/stores/storeInformationStore";

import { 
  COLUMNS_IDENTIFIERS, 
  DEFAULT_VALUES,
  ORDER_ITEM_STATUS,
  ORDER_STATUS,
  USER_ROLES
} from "../../../components/components.constants";

import PropTypes from "prop-types";
import { TenantContext } from "../../../templates/context/TenantContext";
import { UpdatingModal } from "../../../components/SharedComponents/UpdatingModal/UpdatingModal";
import { useSeetingsViewModel } from "../../../viewModel/useSettingsViewModel";

import { buildModalConfig, renderModal } from "../../../viewModel/builder";
import { AddItemModal } from "../sharedComponents/AddItemModal";
import { NewItemsValidationModal } from "../sharedComponents/NewItemsValidationModal";
import { UnsavedItemsWarningModal } from "../sharedComponents/UnsavedItemsWarningModal";

const { t } = i18n;

const generateOrderItemPatchPayload = (data, inputData = {}, hasToRemoveItem = false) => {
  const { 
    storeId, 
    orderId, 
    supplierId, 
    productId, 
    orderDate, 
    purchaseRecommendationId,
    effectivePurchase,
    packToUnitRatio,
    prefixId,
    userId,
    productName,
    isItemRecentlyAdded,
    prefixName,
    purchaseStatus
  } = data;

  if (!storeId || !orderId || !supplierId || !productId) return null;

  const payloadForOrderItensUpate = {
    storeId,
    orderId,
    supplierId,
    userId,
    productId,
    orderDate,
    purchaseRecommendationId,
    effectivePurchase: inputData.effectivePurchase ?? effectivePurchase,
    packToUnitRatio: inputData.packToUnitRatio ?? packToUnitRatio,
    prefixId: inputData.prefixId ?? prefixId,
    itemCtx: {
      hasToRemoveItem
    },
  };

  if (isItemRecentlyAdded) {
    return {
      ...payloadForOrderItensUpate,
      prefixName,
      orderItemStatus: ORDER_ITEM_STATUS.ACTIVE,
      productName,
      purchaseStatus,
      itemCtx: {
        isItemRecentlyAdded: isItemRecentlyAdded
      }
    };
  }

  return payloadForOrderItensUpate;
};

const updateInputs = async ({ inputs, orderItemsToUse, patchOrders, refetchOrderItems, userId }) => {
  const productsIds = Object.keys(inputs);

  if (productsIds.length === 0) {
    return { success: false, shouldVerify: false, updatedCount: 0, itemsSkipped: 0, skippedDetails: [] };
  }

  const payloads = productsIds
    .map(productId => {
      const productData = orderItemsToUse.find(item => item.productId === Number(productId));
      if (!productData) return null;
      return generateOrderItemPatchPayload({ ...productData, userId }, inputs[productId]);
    })
    .filter(Boolean);

  try {
    const result = await patchOrders.updateOrderItemStatus(payloads);
    await refetchOrderItems();

    return {
      success: true,
      shouldVerify: true,
      updatedCount: result?.updatedCount ?? payloads.length,
      itemsSkipped: result?.itemsSkipped ?? 0,
      skippedDetails: result?.skippedDetails ?? []
    };
  } catch (error) {
    await refetchOrderItems();
    throw error;
  }
};

const removeCheckedProducts = async ({ checkedProducts, orderItemsToUse, patchOrders, userId }) => {
  if (checkedProducts.length === 0) {
    return { success: false, shouldVerify: false, updatedCount: 0, itemsSkipped: 0, skippedDetails: [] };
  }

  const payloads = checkedProducts
    .map(productId => {
      const productData = orderItemsToUse.find(item => item.productId === productId);
      if (!productData) return null;
      return generateOrderItemPatchPayload({...productData, userId }, {}, true);
    })
    .filter(Boolean);

  try {
    const result = await patchOrders.updateOrderItemStatus(payloads);

    return {
      success: true,
      shouldVerify: true,
      updatedCount: result?.updatedCount ?? payloads.length,
      itemsSkipped: result?.itemsSkipped ?? 0,
      skippedDetails: result?.skippedDetails ?? []
    };
  } catch (error) {
    throw error;
  }
};

const OrderDetailsModal = ({ ctx, uiState, actions }) => {

  const { 
    onClose, 
    setInputs,
    patchOrders,
    refetchOrderItems,
    setShowInputs,
    setShowSuccessModal, 
    setCheckedProducts,
    setLocalOrderItems
  } = actions;  

  const { 
    tableInfo, 
    columns,
    orderId,
    orderInfo,
    orderItemsToUse,
    userId
  } = ctx;

  const { 
    inputs = {}, 
    showInputs, 
    saveLoading, 
    isLoadingItems,
    checkedProducts = [],
    isPatchingOrder,
  } = uiState;

  const { userInfo, username } = useContext(TenantContext);

  const [showCancelModal, setShowCancelModal] = useState(false);

  const [qtyOfItemsToRemove, setQtyOfItemsToRemove] = useState(0);
  const [qtyOfItensToUpdate, setQtyOfItemsToUpdate] = useState(0);

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictProducts, setConflictProducts] = useState([]);

  const [showSkippedItemsModal, setShowSkippedItemsModal] = useState(false);
  const [skippedItemsInfo, setSkippedItemsInfo] = useState(null);

  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemSearchQuery, setAddItemSearchQuery] = useState("");

  const [showNewItemsValidationModal, setShowNewItemsValidationModal] = useState(false);
  const [invalidNewItems, setInvalidNewItems] = useState([]);

  const unsavedItems = orderItemsToUse.filter(item => item.isItemRecentlyAdded);

  const [showUnsavedItemsModal, setShowUnsavedItemsModal] = useState(unsavedItems.length > 0);

  const upperLineInformation = {
    region: tableInfo?.region || "",
    city: tableInfo?.city || "Cidade",
    name: tableInfo?.name || t("store"),
    cnpj: tableInfo?.cnpj || "00.000.000/0000-00",
    address: tableInfo?.address || t("address")
  }

  const dateToUse = () => {
    const rawValue = orderInfo?.uiPurchaseDate || orderInfo?.orderDate;
    const date = new Date(rawValue);

    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const bottomLineInformation = {
    supplierName: orderInfo?.supplierName ?? DEFAULT_VALUES.NOT_APPLY,
    name: username ?? userInfo?.tenant?.name,
    orderDate: dateToUse(),
    orderId: orderInfo?.orderId
  };

  const onInputsSave = async () => {
    try {

      const newItemsStillInvalid = orderItemsToUse.filter(item => {
        if (!item.isItemRecentlyAdded) return false;
        const inputValue = inputs[item.productId]?.effectivePurchase;
        const originalValue = item.effectivePurchase;
        return !inputValue && (!originalValue || originalValue === 0);
      });

      if (newItemsStillInvalid.length > 0) {
        setInvalidNewItems(newItemsStillInvalid);
        setShowNewItemsValidationModal(true);
        return null;
      }

      setQtyOfItemsToRemove(checkedProducts.length);
      setQtyOfItemsToUpdate(Object.keys(inputs).length);

      const hasInputs = Object.keys(inputs).length > 0;
      const hasCheckedProducts = checkedProducts.length > 0;

      if (!hasInputs && !hasCheckedProducts) return null;

      const inputProductIds = Object.keys(inputs).map(Number) || [];
      const conflictingProducts = checkedProducts.filter(productId => inputProductIds.includes(productId)) || [];

      if (conflictingProducts?.length > 0) {
        setConflictProducts(conflictingProducts);
        setShowConflictModal(true);
        return null;
      }

      let inputsResult, removalResult;
      
      if (hasInputs) {
        inputsResult = await updateInputs({ inputs, orderItemsToUse, patchOrders, refetchOrderItems, userId });
      }
      
      if (hasCheckedProducts) {
        removalResult = await removeCheckedProducts({ checkedProducts, patchOrders, orderItemsToUse, userId });
      }

      const totalSkipped = (inputsResult?.itemsSkipped || 0) + (removalResult?.itemsSkipped || 0);
      const totalUpdated = (inputsResult?.updatedCount || 0) + (removalResult?.updatedCount || 0);

      if (totalSkipped > 0) {
        const allSkippedDetails = [
          ...(inputsResult?.skippedDetails || []),
          ...(removalResult?.skippedDetails || [])
        ];
        
        setSkippedItemsInfo({
          skippedCount: totalSkipped,
          updatedCount: totalUpdated,
          skippedDetails: allSkippedDetails
        });
        setShowSkippedItemsModal(true);
      } else if (totalUpdated > 0) {
        setShowSuccessModal(true);
      }

      setCheckedProducts([]);
      setInputs([]);
      setShowInputs(false);

      return {
        inputs: inputsResult,
        removal: removalResult
      };
    } catch (error) {
      setCheckedProducts([]);
      
      throw error;
    } finally {
      setCheckedProducts([]);
      setInputs([]);
    }
  };

  // const addItensToOrder = ({ itemToAdd, currentOrderState }) => {
  //   const alreadyExists = currentOrderState.some(
  //     item => item.productId === itemToAdd.productId
  //   );
  //   if (alreadyExists) return;

  //   const enrichedItem = {
  //     ...itemToAdd,
  //     orderId: orderInfo?.orderId,
  //     supplierId: orderInfo?.supplierId,
  //     orderDate: new Date().toISOString(),
  //     isItemRecentlyAdded: true,
  //   };

  //   setLocalOrderItems(prev => [...prev, enrichedItem]);
  // };

  const addItensToOrder = ({ itemToAdd }) => {
    const enrichedItem = {
      ...itemToAdd,
      orderId: orderInfo?.orderId,
      supplierId: orderInfo?.supplierId,
      orderDate: new Date().toISOString(),
      isItemRecentlyAdded: true,
    };

    setLocalOrderItems(prev => {
      const alreadyExists = prev.some(item => item.productId === itemToAdd.productId);
      if (alreadyExists) return prev;
      return [...prev, enrichedItem];
    });
  };

  const modals = buildModalConfig({
    mode: "orderDetailsUpdatingStates",
    actions: {
      onCloseConflict: () => setShowConflictModal(false),
      onCloseSkipped: () => setShowSkippedItemsModal(false),
      setShowCancelModal,
      setShowInputs,
      setInputs,
      setShowAddItemModal,
      setAddItemSearchQuery,
      addItensToOrder: addItensToOrder,
      setShowNewItemsValidationModal: setShowNewItemsValidationModal,
      setShowUnsavedItemsModal: setShowUnsavedItemsModal
    },
    ctx: {
      t: t,
      searchQuery: addItemSearchQuery,
      supplierId: orderInfo?.supplierId,
      orderItemsToUse: orderItemsToUse,
      unsavedItems: unsavedItems
    },
    uiState: {
      qtyOfItemsToRemove,
      qtyOfItensToUpdate,
      conflictProducts,
      skippedItemsInfo,
      isPatchingOrder,
      showConflictModal,
      showSkippedItemsModal,
      patchOrdersError: patchOrders.errorOnUpdate,
      showCancelModal,
      showAddItemModal,
      showNewItemsValidationModal: showNewItemsValidationModal,
      invalidNewItems: invalidNewItems,
      showUnsavedWarningModal: showUnsavedItemsModal
    },

    components: { 
      ModalToControlUpdateAndRemovalOperations: UpdatingModal,
      ModalToCancelEditModeOnItem: CancelModalButton,
      ModalToAddItems: AddItemModal,
      ModalToValidateNewItems: NewItemsValidationModal,
      ModalToWarnUserThatExistsUnsavedItems: UnsavedItemsWarningModal
    },
  });
    
  if (!orderId) return null;
  if (isLoadingItems) return <LoadingContent label={t("loadingOrderItens")} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
      backdropFilter: "blur(2px)",
    }}>
      <div 
        id={`modal-${orderId}`} 
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          width: "1017px",
          height: "865px",
          position: "relative",
        }}>   

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="sm"
            type="outlined"
            onClick={() => {
              if (showInputs) setShowInputs(false)
              onClose()
            }}>
            <CloseIcon size={15} />
          </Button>
        </div>
        
        <OrderDetailsHeader 
          ctx={{ upperLineInformation: upperLineInformation, bottomLineInformation: bottomLineInformation }}
          uiState={{ shouldShowOrderId: true }}/>

        <div style={{
          width: "950px",
          height: "0px",
          margin: "20px auto",
          borderTop: "3px solid #E5E2E2",
          transform: "rotate(180deg)",
        }} />

        <OrderDetailsTable 
          actions={{
            onAddItem: () => setShowAddItemModal(true)
          }}
          ctx={{
            columns: columns,
            tableSourceData: orderItemsToUse,
            tableSourceMetaData: {
              orderId,
              enableSorting: true,
              hideOnExport: [COLUMNS_IDENTIFIERS.PREFIX_NAME],
              rowIdKey: "productId"
            }
          }}
          uiState={{
            headerStyle: { textAlign: 'center' },
            cellStyle: { textAlign: 'left' },
            showAddNewItemRow: showInputs
          }}/>

        <OrderDetailsActions
          actions={{
            setShowCancelModal: setShowCancelModal,
            setShowInputs: setShowInputs,
            onInputsSave: onInputsSave
          }}
          ctx={{
            orderId: orderId,
            storeName: tableInfo?.name,
            orderDate: dateToUse(),
            tableInfo: tableInfo,
            currentSupplierName: orderInfo?.supplierName,
            hideOnExport: [COLUMNS_IDENTIFIERS.PREFIX_NAME]
          }}          
          uiState={{
            saveLoading: saveLoading,
            customDisabledCondition: orderInfo?.orderStatus === ORDER_STATUS.REALIZED ? true : false,
            isStoresTable: true,
            showInputs: showInputs,
            buttonsDisplayMode: "order_details_modal",
            showDownloadButtonForXLS: true,
            cameraIcon: CameraIcon,
            pdfIcon: PdfIcon,
            xlsIcon: <XlsIcon size={28} />                     
          }}/>

        {renderModal(modals)}

      </div>
    </div>
  );
};

export function PurchaseHistory() {
  const [activeTab, setActiveTab] = useState(ORDER_STATUS.REALIZED);

  const { selectedStoreId, selectedStoreNumber, setStore } = useStoreInformation();

  const vmSettings = useSeetingsViewModel({ shouldFetchUserCtx: true });
  const vm = usePurchaseRecommendationViewModel({
    shouldFetchPurchaseRecommendationData: false,
    shouldFetchOrdersData: true,
    shouldFetchAvailableItens: false
  });

  const modals = buildModalConfig({
    mode: "purchaseHistory",
    actions: vm?.actions,
    ctx: {
      ...vm.ctx,
      userId: vmSettings?.ctx?.userId,
      selectedStoreId,
      t,
    },
    components: {
      ModalToDisplayOrder: PurchaseHistory.ModalToInteractWithOrders,
      ModalToCancelOrder: CancelModalButton,
      OrderCancellationSuccessModal: UpdatingModal
    },
    uiState: vm.uiState,
  });

  const countByStatus = (status) => vm.ctx.filteredOrders?.filter((o) => o.orderStatus === status).length ?? 0;

  const TAB_OPTIONS = [
    {
      label: t("all"),
      value: null,
      count: vm.ctx.filteredOrders?.length ?? 0,
    },
    {
      label: t("realized"),
      value: ORDER_STATUS.REALIZED,
      count: countByStatus(ORDER_STATUS.REALIZED),
      color: "#f59e0b",
    },
    {
      label: t("done"),
      value: ORDER_STATUS.CONCLUDED,
      count: countByStatus(ORDER_STATUS.CONCLUDED),
      color: "#10b981",
    },
    {
      label: t("canceled"),
      value: ORDER_STATUS.CANCELED,
      count: countByStatus(ORDER_STATUS.CANCELED),
      color: "#ef4444",
    },
  ];

  const filteredOrdersByTab = activeTab
    ? vm.ctx.filteredOrders?.filter((order) => order.orderStatus === activeTab)
    : vm.ctx.filteredOrders;

  if (vm.uiState.isLoadingStores || vm.uiState.isLoadingOrders)
    return (
      <LoadingContent
        label={t("loadingOrdersByStores")}
        type={LOADING_CONTENT_TYPE.API_PAGE_DATA}
      />
    );

  return (
    <Default>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          inlineSize: "100%",
          padding: "2rem 0 0 1rem",
          flexDirection: 'column'
        }}
      >
        {vmSettings.ctx.userRole !== USER_ROLES.SHOPKEEPER && (
          <HeaderWithFilters
            actions={{
              setSelectedSecondaryFilterValues: setStore,
              setShowInputs: vm.actions.setShowInputs,
            }}
            ctx={{
              selectedSecondaryFilterValues: selectedStoreNumber || [],
              allSecondaryOptions: vm.ctx.allStoresNames || [],
              allPrimaryOptions: vm.ctx.allRegionsNames || [],
              labels: {
                titlePage: t("purchaseOrderHistory"),
                firstSelect: t("region"),
                secondSelect: t("store"),
                filterAction: t("selectAction"),
              },
            }}
            uiState={{
              isLoadingStores: vm.uiState.isLoadingStores,
              showInputs: vm.uiState.showInputs,
            }}
            mode={HEADER_STRATEGY_CONSTANTS.DEFAULT}/>
        )}

        {/* Abas */}
        <div
          style={{
            display: "flex",
            gap: "0",
            borderBottom: "2px solid #e5e7eb",
            marginTop: "0.5rem",
          }}
        >
          {TAB_OPTIONS.map((tab) => {
            const isActive = activeTab === tab.value;
            const accentColor = tab.color ?? "#FF6600";

            return (
              <button
                key={tab.value ?? "all"}
                onClick={() => setActiveTab(tab.value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.6rem 1.25rem",
                  fontWeight: isActive ? "700" : "400",
                  fontSize: "0.85rem",
                  borderBottom: isActive
                    ? `2px solid ${accentColor}`
                    : "2px solid transparent",
                  color: isActive ? accentColor : "#6b7280",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  marginBottom: "-2px",
                  transition: "color 0.15s, border-color 0.15s",
                }}
              >
                {tab.label}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "1.25rem",
                    height: "1.25rem",
                    padding: "0 0.35rem",
                    borderRadius: "9999px",
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    backgroundColor: isActive ? accentColor : "#e5e7eb",
                    color: isActive ? "#fff" : "#6b7280",
                    transition: "background-color 0.15s, color 0.15s",
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <OrderDetailsTable
          ctx={{
            columns: vm.ctx.getPurchaseHistoryColumns(),
            tableSourceData: filteredOrdersByTab,
            tableSourceMetaData: {
              enableSorting: true,
              sortingMode: {
                id: COLUMNS_IDENTIFIERS.ORDER_ID,
                desc: true,
              },
            },
          }}
          uiState={{
            hasWhiteBackground: true,
            headerWrapperStyle: {
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0",
            }
          }}
        />

        {/* {vm.uiState.isFetchingOrders && (
          <LoadingContent
            label={t("loadingOrdersByStores")}
            type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}
          />
        )} */}
        {vm.uiState.isFetchingOrders && (
  <div style={{ padding: "12px", background: "yellow", fontWeight: "bold", fontSize: "16px" }}>
    ATUALIZANDO PEDIDOS - isFetchingOrders = {String(vm.uiState.isFetchingOrders)}
  </div>
)}

        {renderModal(modals)}
      </div>
    </Default>
  );
};

PurchaseHistory.ModalToInteractWithOrders = OrderDetailsModal;

OrderDetailsModal.propTypes = {
  acitons: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape(),
};